import React, { useEffect, useState } from 'react'

import type { TSimTableChatSummaryDto } from '@homepal/api-contract'

import Box from 'components_new/atoms/Box'
import Divider from 'components_new/atoms/Divider'
import Icon from 'components_new/atoms/Icon'
import Skeleton from 'components_new/atoms/Skeleton'
import Text from 'components_new/atoms/Text'

import SearchField from 'components_new/molecules/SearchField'

import {
  ChatDay,
  chatDay,
  chatWhen
} from 'components_new/organisms/ChatBox/historyLabels'

import { getSimTableChats } from 'redux/api/DataPlatform/tables/v4/chat'

const DAY_LABEL: Record<ChatDay, string> = {
  today: 'Idag',
  yesterday: 'Igår',
  earlier: 'Tidigare'
}

const DAY_ORDER: ChatDay[] = ['today', 'yesterday', 'earlier']

const messagesLabel = (count: number) =>
  (count === 1 ? '1 meddelande' : `${count} meddelanden`)

const ChatRow = (props: {
  chat: TSimTableChatSummaryDto
  onSelect: () => void
}) => {
  const { chat, onSelect } = props

  return (
    <Box
      onClick={onSelect}
      sx={{
        alignItems: 'flex-start',
        cursor: 'pointer',
        display: 'flex',
        gap: 1.5,
        px: 2,
        py: 1.25,
        '&:hover': { bgcolor: 'action.hover' }
      }}
    >
      <Box
        sx={{
          alignItems: 'center',
          bgcolor: 'action.hover',
          borderRadius: 1,
          display: 'flex',
          flex: '0 0 auto',
          height: 32,
          justifyContent: 'center',
          mt: 0.25,
          width: 32
        }}
      >
        <Icon color="disabled" fontSize="small" name="Chat" />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Text color="text.primary" noWrap={true} variant="subtitle2">
          {chat.title}
        </Text>
        <Text color="text.secondary" variant="caption" sx={{ display: 'block' }}>
          {`${chatWhen(chat.updated_at)} · ${messagesLabel(chat.message_count)}`}
        </Text>
      </Box>

      <Icon
        color="disabled"
        fontSize="small"
        name="ArrowForward"
        sx={{ flex: '0 0 auto', mt: 0.75 }}
      />
    </Box>
  )
}

interface ChatHistoryProps {
  tableId: string
  onBack: () => void
  onSelect: (chatId: string) => void
}

/**
 * The signed-in user's earlier chats on the table, grouped by the day they
 * were last active. Fetched every time the list is opened, so a chat just
 * finished is in it.
 */
const ChatHistory = (props: ChatHistoryProps) => {
  const { tableId, onBack, onSelect } = props

  const [chats, setChats] = useState<TSimTableChatSummaryDto[]>([])
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState(false)
  const [search, setSearch] = useState('')

  useEffect(() => {
    let active = true

    setLoading(true)
    setFailed(false)

    getSimTableChats(tableId)
      .then((response) => {
        if (active) setChats(response.data.data)
      })
      .catch(() => {
        if (active) setFailed(true)
      })
      .finally(() => {
        if (active) setLoading(false)
      })

    return () => {
      active = false
    }
  }, [tableId])

  const query = search.trim().toLowerCase()
  const matches = query
    ? chats.filter((chat) => chat.title.toLowerCase().includes(query))
    : chats
  const groups = DAY_ORDER.map((day) => ({
    day,
    chats: matches.filter((chat) => chatDay(chat.updated_at) === day)
  })).filter((group) => group.chats.length > 0)

  const emptyText = failed
    ? 'Kunde inte hämta tidigare chattar.'
    : query
      ? 'Inga chattar matchar.'
      : 'Inga tidigare chattar på den här tabellen.'

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0
      }}
    >
      <Box sx={{ px: 2, pt: 1 }}>
        <Text
          color="text.secondary"
          onClick={onBack}
          variant="body2"
          sx={{
            alignItems: 'center',
            cursor: 'pointer',
            display: 'inline-flex',
            gap: 1,
            '&:hover': { color: 'text.primary' }
          }}
        >
          <Icon fontSize="small" name="ArrowBack" />
          Tillbaka
        </Text>

        <Text color="text.primary" variant="h6" sx={{ mt: 1 }}>
          Tidigare chattar
        </Text>

        <Text color="text.secondary" variant="body2" sx={{ mb: 1.5, mt: 0.5 }}>
          Dina egna chattar på den här tabellen. Öppna en för att läsa den
          eller skriva vidare.
        </Text>

        <SearchField
          onClear={() => setSearch('')}
          onSearch={setSearch}
          placeholder="Sök i tidigare chattar"
          size="small"
          sx={{ width: '100%' }}
          value={search}
        />
      </Box>

      <Divider sx={{ mt: 1.5 }} />

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', py: 1 }}>
        {loading ? (
          <Box sx={{ px: 2 }}>
            <Skeleton height={56} />
            <Skeleton height={56} />
            <Skeleton height={56} />
          </Box>
        ) : null}

        {!loading && groups.length === 0 ? (
          <Text
            align="center"
            color="text.secondary"
            variant="body2"
            sx={{ px: 2, py: 4 }}
          >
            {emptyText}
          </Text>
        ) : null}

        {groups.map((group) => (
          <Box key={group.day} sx={{ mb: 1 }}>
            <Text
              color="text.secondary"
              variant="overline"
              sx={{ display: 'block', px: 2, py: 1 }}
            >
              {DAY_LABEL[group.day]}
            </Text>

            {group.chats.map((chat) => (
              <ChatRow
                chat={chat}
                key={chat.id}
                onSelect={() => onSelect(chat.id)}
              />
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  )
}

export default ChatHistory
